/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { Tweet, TweetProps } from './Tweet';

export interface ScrollingTweetsSectionProps {
  backgroundColor:
    | typeof COLOR_KEYS.blue10
    | typeof COLOR_KEYS.bolt
    | typeof COLOR_KEYS.neutral100
    | typeof COLOR_KEYS.peach;
  heading: string;
  tweets: Omit<TweetProps, 'ariaHidden'>[];
}

const Styles = labelStyles('ScrollingTweetsSection', {
  root: (backgroundColor: ScrollingTweetsSectionProps['backgroundColor']) =>
    css({
      backgroundColor: Colors[backgroundColor],
      padding: '64px 0 56px',

      [MEDIA_MIN_WIDTH.small]: {
        padding: '80px 0 72px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '96px 0 88px',
      },
    }),

  heading: css({
    ...Typography.pageHeader7,
    color: Colors.earth,
    textAlign: 'center',
    maxWidth: '320px',
    margin: '0 auto 48px',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '640px',
      marginBottom: '64px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.pageHeader5,
      maxWidth: '768px',
      marginBottom: '80px',
    },
  }),

  scroller: css({
    padding: '0 20px',
    overflowX: 'scroll',
    willChange: 'scroll-position',

    // Hide scrollbar
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  }),

  tweets: css({
    display: 'flex',
    width: 'fit-content',
  }),

  tweetStack: css({
    '& + &': {
      marginLeft: '20px',
    },
  }),
});

export function ScrollingTweetsSection(props: ScrollingTweetsSectionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tweetsRef = useRef<HTMLDivElement>(null);
  const previousTimestampRef = useRef<DOMHighResTimeStamp>(0);
  const animationFrameRef = useRef<number>();
  const isIntersectingRef = useRef<boolean>(false);

  const autoScroll = useCallback((timestamp: DOMHighResTimeStamp) => {
    if (scrollerRef.current && timestamp - previousTimestampRef.current > 25) {
      scrollerRef.current.scrollBy(1, 0);
      previousTimestampRef.current = timestamp;
    }
    animationFrameRef.current = requestAnimationFrame(autoScroll);
  }, []);

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    const tweets = tweetsRef.current;

    if (!tweets || !scroller) {
      return;
    }

    const resetPoint = tweets.clientWidth / 2 + 10;

    if (scroller.scrollLeft <= 0 || scroller.scrollLeft >= resetPoint) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        /*
          To achieve the infinite looping effect, when the scroll position
          reaches the last non-duplicated stack of tweets (plus spacing), the
          scroll position should reset to the beginning. Similarly, when
          scrolling backwards and the scroll position reaches 0, the scroll
          position should jump to the end of the non-duplicated tweets.
        */
        scroller.scrollTo(scroller.scrollLeft <= 0 ? resetPoint - 1 : 1, 0);

        if (
          isIntersectingRef.current &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          animationFrameRef.current = requestAnimationFrame(autoScroll);
        }
      });
    }
  }, [autoScroll]);

  useEffect(() => {
    const root = rootRef.current;

    if (
      !root ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        isIntersectingRef.current = isIntersecting;
        if (isIntersecting) {
          animationFrameRef.current = requestAnimationFrame(autoScroll);
        } else {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(root);

    return () => {
      observer.unobserve(root);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [autoScroll]);

  const tweetStacks = useMemo(() => {
    return props.tweets.reduce(
      (stacks: ScrollingTweetsSectionProps['tweets'][], tweet, tweetIndex) => {
        const stackIndex = Math.floor(tweetIndex / 2);

        if (!stacks[stackIndex]) {
          stacks[stackIndex] = [];
        }

        stacks[stackIndex].push(tweet);

        return stacks;
      },
      [],
    );
  }, [props.tweets]);

  return (
    <div css={Styles.root(props.backgroundColor)} ref={rootRef}>
      <h2 css={Styles.heading}>{props.heading}</h2>
      <div css={Styles.scroller} onScroll={handleScroll} ref={scrollerRef}>
        <div css={Styles.tweets} ref={tweetsRef}>
          {/*
            The tweets are rendered twice so that when the last tweets scroll
            into view, you begin to see the first tweets again before the scroll
            position is reset
           */}
          {[false, true].map((ariaHidden, renderIndex) =>
            tweetStacks.map((stack, stackIndex) => (
              <div css={Styles.tweetStack} key={`${renderIndex}-${stackIndex}`}>
                {stack.map((tweet, tweetIndex) => (
                  <Tweet
                    {...tweet}
                    ariaHidden={ariaHidden}
                    key={`${renderIndex}-${stackIndex}-${tweetIndex}-${tweet.url}`}
                  />
                ))}
              </div>
            )),
          )}
        </div>
      </div>
    </div>
  );
}
